import tippy from "tippy.js";

// Build the @tiptap/extension-mention `suggestion` config shared by every
// editor that supports community mentions. The Mention *node* config
// (HTMLAttributes/renderText/renderHTML) stays with each caller because
// persistence differs per context (plain text vs Action Text HTML).
//
// communityId: string - used to build the member search URL.
// appendTo:    () => Element - tippy mount point (e.g. dialog or body).
export function buildMentionSuggestion({ communityId, appendTo }) {
  return {
    allowSpaces: false,

    items: async ({ query }) => {
      try {
        const response = await fetch(
          `/communities/${communityId}/mentions/search?q=${encodeURIComponent(query)}`,
        );
        if (!response.ok) return [];
        return await response.json();
      } catch {
        return [];
      }
    },

    render: () => {
      let popup;
      let dropdown;
      let selectedIndex = 0;
      let items = [];
      let commandFn = null;

      return {
        onStart: (props) => {
          items = props.items;
          commandFn = props.command;
          selectedIndex = 0;

          dropdown = createDropdownElement(items, selectedIndex, commandFn);

          popup = tippy("body", {
            getReferenceClientRect: props.clientRect,
            content: dropdown,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "bottom-start",
            maxWidth: 340,
            appendTo,
          })[0];
        },

        onUpdate: (props) => {
          items = props.items;
          commandFn = props.command;
          selectedIndex = 0;

          updateDropdownElement(dropdown, items, selectedIndex, commandFn);
          popup?.setProps({ getReferenceClientRect: props.clientRect });
        },

        onKeyDown: (props) => {
          const { event } = props;

          if (event.key === "ArrowDown") {
            selectedIndex = (selectedIndex + 1) % items.length;
            updateDropdownSelection(dropdown, selectedIndex);
            return true;
          }

          if (event.key === "ArrowUp") {
            selectedIndex = (selectedIndex - 1 + items.length) % items.length;
            updateDropdownSelection(dropdown, selectedIndex);
            return true;
          }

          if (event.key === "Enter") {
            // NOTE: the suggestion plugin calls onKeyDown with only
            // { view, event, range } — there is no props.command here. Use the
            // commandFn captured in onStart/onUpdate instead.
            if (items[selectedIndex] && commandFn) {
              commandFn(mentionAttrs(items[selectedIndex]));
            }
            return true;
          }

          if (event.key === "Escape") {
            popup?.hide();
            return true;
          }

          return false;
        },

        onExit: () => {
          popup?.destroy();
          dropdown = null;
        },
      };
    },
  };
}

// Convert a search result into Mention node attrs. The label joins name parts
// with a non-breaking space ( ) so multi-word names don't wrap — purely
// cosmetic; the backend resolves mentions by id, never by label.
export function mentionAttrs(item) {
  return {
    id: item.id,
    label: item.display_name_plain?.replace(/\s+/g, " ") || item.username,
  };
}

function createDropdownElement(items, selectedIndex, commandFn) {
  const container = document.createElement("div");
  container.className = "mention-dropdown";
  renderItems(container, items, selectedIndex, commandFn);
  return container;
}

function updateDropdownElement(container, items, selectedIndex, commandFn) {
  if (!container) return;
  container.innerHTML = "";
  renderItems(container, items, selectedIndex, commandFn);
}

function updateDropdownSelection(container, selectedIndex) {
  if (!container) return;
  const els = container.querySelectorAll(".mention-dropdown__item");
  els.forEach((el, i) =>
    el.classList.toggle("is-selected", i === selectedIndex),
  );
  els[selectedIndex]?.scrollIntoView({ block: "nearest" });
}

function renderItems(container, items, selectedIndex, commandFn) {
  if (!items.length) return;

  items.forEach((item, index) => {
    const el = document.createElement("button");
    el.type = "button";
    el.className = `mention-dropdown__item${index === selectedIndex ? " is-selected" : ""}`;
    el.addEventListener("click", () => commandFn(mentionAttrs(item)));

    if (item.avatar_url) {
      const img = document.createElement("img");
      img.src = item.avatar_url;
      img.className = "mention-dropdown__avatar";
      img.alt = "";
      el.appendChild(img);
    } else {
      const placeholder = document.createElement("div");
      placeholder.className = "mention-dropdown__avatar-placeholder";
      placeholder.textContent = (
        item.display_name_plain ||
        item.username ||
        "?"
      )
        .charAt(0)
        .toUpperCase();
      el.appendChild(placeholder);
    }

    const userInfo = document.createElement("div");
    userInfo.className = "mention-dropdown__user-info";
    const nameSpan = document.createElement("span");
    nameSpan.className = "mention-dropdown__name";
    nameSpan.textContent =
      item.display_name_plain || item.display_name || item.username;
    userInfo.appendChild(nameSpan);
    el.appendChild(userInfo);

    container.appendChild(el);
  });
}
